import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button, TextInput } from '../../components';
import { LogoMark } from '../../components/branding/Logo';
import { apiClient } from '../../api/client';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await apiClient.post('/api/v1/auth/forgot-password', { email });
      setIsSubmitted(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="max-w-sm mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-background-card rounded-xl p-8 shadow-md text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 500 }}
            className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-8 h-8 text-success" />
          </motion.div>
          <h2 className="text-2xl font-semibold text-text-primary mb-2">Check your email</h2>
          <p className="text-text-secondary mb-6">
            If an account exists for {email}, you'll receive a password reset link shortly.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
          >
            <ArrowLeft size={16} />
            Back to login
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-sm mx-auto">
      <div className="bg-background-card rounded-xl p-6 md:p-8 shadow-md">
        <div className="text-center mb-6 md:mb-8">
          <LogoMark className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-3 md:mb-4 md:hidden" />
          <h2 className="text-xl md:text-2xl font-semibold text-text-primary mb-1 md:mb-2">Forgot password?</h2>
          <p className="text-sm md:text-base text-text-secondary">
            Enter your email and we'll send you a reset link.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
          <TextInput
            type="email"
            label="Email"
            placeholder="you@example.com"
            icon={Mail}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />

          {error && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-md">
              <p className="text-sm text-error text-center">{error}</p>
            </div>
          )}

          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </div>
        </form>
      </div>

      <p className="text-center mt-6 text-text-secondary">
        <Link to="/login" className="text-primary font-medium inline-flex items-center gap-1">
          <ArrowLeft size={14} />
          Back to login
        </Link>
      </p>
    </div>
  );
}
